/**
 * Language Settings Component
 *
 * Allows users to change their preferred language for the application.
 * Syncs with backend API and updates UI immediately (optimistic update).
 *
 * TASK-168: Settings Page - Language Tab
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage, Language } from '../../contexts/LanguageContext';
import { LanguageSwitcher } from '../forms/LanguageSwitcher';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { toast } from 'sonner';

export default function LanguageSettings() {
  const { t } = useTranslation('settings');
  const { language, setLanguage, availableLanguages } = useLanguage();
  const [saving, setSaving] = useState(false);

  const handleLanguageChange = async (newLang: Language) => {
    setSaving(true);
    try {
      await setLanguage(newLang);
      toast.success(t('language.saved', 'Language preference updated successfully'));
    } catch (error) {
      console.error('Failed to change language:', error);
      toast.error(t('language.error', 'Failed to update language preference'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('language.title', 'Language Preferences')}</CardTitle>
        <CardDescription>
          {t('language.description', 'Choose your preferred language for the application interface')}
        </CardDescription>
      </CardHeader>

      <CardContent>
        <div className="space-y-6">
          {/* Language Selector */}
          <div>
            <label className="text-sm font-medium text-gray-900 mb-2 block">
              {t('language.select', 'Select Language')}
            </label>
            {/* TASK-607: language NAMES, not flags — a flag is a country, not a
                language (CH users were shown 🇬🇧 for English). */}
            <LanguageSwitcher
              currentLanguage={language}
              availableLanguages={availableLanguages}
              onLanguageChange={handleLanguageChange}
              variant="dropdown"
              disabled={saving}
            />
          </div>

          {/* Language Preview */}
          <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
            <h4 className="font-medium text-sm text-gray-900 mb-3">
              {t('language.preview', 'Preview')}
            </h4>
            <div className="text-sm space-y-2 text-gray-600">
              <div className="flex justify-between">
                <span>{t('language.date_format', 'Date format')}:</span>
                <span className="font-medium text-gray-900">
                  {new Date().toLocaleDateString(language)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>{t('language.number_format', 'Number format')}:</span>
                <span className="font-medium text-gray-900">
                  {(1234.56).toLocaleString(language)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>{t('language.currency_format', 'Currency format')}:</span>
                <span className="font-medium text-gray-900">
                  CHF {(1234.56).toLocaleString(language, { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </div>

          {/* Info */}
          <p className="text-xs text-gray-500">
            {t('language.info', 'Language changes apply immediately and are saved to your account.')}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
